import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import type { SuggestionItem } from "@/features/timesheetFlow/components/SuggestionPickerDialog";

function patchSuggestionItems(
  items: SuggestionItem[],
  draftUsedIds?: Set<string>,
  locallyFreedIds?: Set<string>,
): SuggestionItem[] {
  return items.map((item) => {
    if (draftUsedIds?.has(item.id)) {
      return {
        ...item,
        disabled: true,
        disabledReason: item.disabledReason || "Bereits übernommen",
      };
    }
    // Locally freed only clears "in this draft" – never unlock hours already claimed elsewhere.
    if (locallyFreedIds?.has(item.id) && item.disabled && item.disabledReason === "Bereits übernommen") {
      return {
        ...item,
        disabled: false,
        disabledReason: undefined,
      };
    }
    if (locallyFreedIds?.has(item.id) && !item.disabled) {
      return item;
    }
    return item;
  });
}

export function TimesheetInlineSuggestionPanel({
  title,
  hint,
  loadItems,
  onImportSelected,
  onImportAll,
  reloadKey,
  draftUsedIds,
  locallyFreedIds,
  hideWhenEmpty = false,
}: {
  title: string;
  hint?: string;
  loadItems: () => Promise<SuggestionItem[]>;
  onImportSelected: (ids: string[]) => void;
  onImportAll: (ids: string[]) => void;
  reloadKey?: string | number;
  draftUsedIds?: Set<string>;
  locallyFreedIds?: Set<string>;
  /** Blendet den Block aus, solange keine Vorschläge geladen sind. */
  hideWhenEmpty?: boolean;
}) {
  const [items, setItems] = useState<SuggestionItem[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const hasLoadedOnceRef = useRef(false);
  const prevReloadKeyRef = useRef(reloadKey);

  useEffect(() => {
    let cancelled = false;
    const showInitialSpinner = !hasLoadedOnceRef.current;
    if (showInitialSpinner) setInitialLoading(true);

    void loadItems()
      .then((list) => {
        if (!cancelled) {
          setItems(list);
          setLoadError(null);
          hasLoadedOnceRef.current = true;
          if (prevReloadKeyRef.current !== reloadKey) {
            setSelected(new Set());
            prevReloadKeyRef.current = reloadKey;
          }
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setItems([]);
          setLoadError(err instanceof Error ? err.message : "Vorschläge konnten nicht geladen werden.");
        }
      })
      .finally(() => {
        if (!cancelled) setInitialLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadItems, reloadKey]);

  const displayItems = useMemo(
    () => patchSuggestionItems(items, draftUsedIds, locallyFreedIds),
    [items, draftUsedIds, locallyFreedIds],
  );
  const selectable = useMemo(() => displayItems.filter((i) => !i.disabled), [displayItems]);

  if (hideWhenEmpty && !loadError && (initialLoading || displayItems.length === 0)) {
    return null;
  }

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-2.5 rounded-xl border border-separator/70 bg-card/70 p-3">
      <div>
        <h4 className="text-sm font-semibold text-label">{title}</h4>
        {hint ? <p className="mt-0.5 text-[11px] leading-snug text-label-secondary">{hint}</p> : null}
      </div>
      <div className="relative max-h-40 overflow-y-auto rounded-lg border border-separator/80 bg-fill-tertiary/20 p-1.5 text-sm">
        {initialLoading && !items.length ? (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        ) : loadError ? (
          <p className="text-status-red">{loadError}</p>
        ) : !displayItems.length ? (
          <p className="text-label-tertiary">Keine Vorschläge verfügbar.</p>
        ) : (
          <ul className="space-y-1">
            {displayItems.map((item) => (
              <li key={item.id}>
                <label
                  className={clsx(
                    "flex cursor-pointer gap-2 rounded-ios px-2 py-1.5",
                    item.disabled ? "cursor-not-allowed opacity-50" : "hover:bg-fill-tertiary",
                  )}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    disabled={item.disabled}
                    checked={selected.has(item.id)}
                    onChange={() => toggle(item.id)}
                  />
                  <span className="min-w-0">
                    <span className="block text-label">{item.label}</span>
                    {item.meta ? (
                      <span className="block text-xs text-label-tertiary">{item.meta}</span>
                    ) : null}
                    {item.disabled && item.disabledReason ? (
                      <span className="block text-xs text-status-red">{item.disabledReason}</span>
                    ) : null}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="w-full sm:w-auto"
          disabled={!selected.size || initialLoading || Boolean(loadError)}
          onClick={() => {
            onImportSelected([...selected]);
            setSelected(new Set());
          }}
        >
          Ausgewählte übernehmen
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="w-full sm:w-auto"
          disabled={!selectable.length || initialLoading || Boolean(loadError)}
          onClick={() => onImportAll(selectable.map((i) => i.id))}
        >
          Alle übernehmen
        </Button>
      </div>
    </div>
  );
}
